import api from './api';
import { sanitizeFile } from '../utils/fileUtils';
// import { computeFileHash } from '../utils/fileHashWorker'; // TODO: re-enable when backend supports dedup

// ==================== MULTIPART UPLOAD CONFIGURATION ====================

/** Files larger than this use multipart chunked upload (10MB) */
const MULTIPART_THRESHOLD = 10 * 1024 * 1024;

/** Max concurrent chunk uploads */
const MAX_CONCURRENT_CHUNKS = 3;

/** Max retries per chunk before failing */
const MAX_CHUNK_RETRIES = 3;

/** localStorage key prefix for resumable upload tracking */
const UPLOAD_STORAGE_PREFIX = 'multipart_upload_';

/** How long to keep upload progress in localStorage (24 hours) */
const UPLOAD_EXPIRY_MS = 24 * 60 * 60 * 1000;

/** Max auto-resume attempts when tab becomes visible again */
const MAX_AUTO_RESUME_ATTEMPTS = 5;

/** Time to wait for tab to become visible before giving up (5 minutes) */
const VISIBILITY_WAIT_TIMEOUT_MS = 5 * 60 * 1000;

// ==================== WAKE LOCK HELPER ====================

let activeWakeLock = null;

const acquireWakeLock = async () => {
    if ('wakeLock' in navigator) {
        try {
            activeWakeLock = await navigator.wakeLock.request('screen');
            activeWakeLock.addEventListener('release', () => {
                activeWakeLock = null;
            });
        } catch (err) {
            console.warn('Wake Lock request failed:', err.message);
        }
    }
};

const releaseWakeLock = () => {
    if (activeWakeLock) {
        activeWakeLock.release().catch(() => {});
        activeWakeLock = null;
    }
};

// Re-acquire wake lock when tab becomes visible again (iOS/Android can release it)
if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && activeWakeLock !== null) {
            acquireWakeLock();
        }
    });
}

// ==================== VISIBILITY HELPER ====================

/**
 * Returns a promise that resolves when the tab becomes visible.
 * If already visible, resolves immediately.
 * Times out after VISIBILITY_WAIT_TIMEOUT_MS to prevent hanging forever.
 */
const waitForVisibility = () => {
    return new Promise((resolve, reject) => {
        if (typeof document === 'undefined') {
            resolve();
            return;
        }
        if (document.visibilityState === 'visible') {
            resolve();
            return;
        }

        let timer;
        const handler = () => {
            if (document.visibilityState === 'visible') {
                clearTimeout(timer);
                document.removeEventListener('visibilitychange', handler);
                // Small delay to let the browser fully resume networking
                setTimeout(resolve, 500);
            }
        };

        document.removeEventListener('visibilitychange', handler);
        document.addEventListener('visibilitychange', handler);

        timer = setTimeout(() => {
            document.removeEventListener('visibilitychange', handler);
            reject(new Error('Upload timed out — app was in the background too long'));
        }, VISIBILITY_WAIT_TIMEOUT_MS);
    });
};

export const mediaService = {
    // ==================== PRESIGNED URL UPLOAD METHODS (NEW) ====================
    
    /**
     * Get a presigned URL for direct S3 upload
     * @param {string} filename - Original filename
     * @param {string} contentType - MIME type of the file
     * @param {string} context - Upload context: 'profile', 'banner', 'post', 'message'
     * @param {number} fileSize - Optional file size for pre-validation
     * @returns {Promise<{uploadId, presignedUrl, s3Key, expiresAt, contentType, maxFileSize}>}
     */
    getPresignedUploadUrl: async (filename, contentType, context, fileSize = null) => {
        const response = await api.post('/media/presign', {
            filename,
            contentType,
            context,
            fileSize
        });
        return response.data;
    },
    
    /**
     * Upload file directly to S3 using presigned URL
     * @param {string} presignedUrl - The presigned PUT URL from getPresignedUploadUrl
     * @param {File} file - The file to upload
     * @param {string} contentType - MIME type (must match what was used to generate presigned URL)
     * @param {function} onProgress - Progress callback (0-100)
     * @returns {Promise<void>}
     */
    uploadToS3: (presignedUrl, file, contentType, onProgress = null) => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable && onProgress) {
                    const percentComplete = Math.round((event.loaded / event.total) * 100);
                    onProgress(percentComplete);
                }
            });
            
            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                } else {
                    console.error('S3 Upload failed:', xhr.status, xhr.statusText);
                    reject(new Error(`S3 upload failed with status ${xhr.status}: ${xhr.statusText}`));
                }
            });
            
            xhr.addEventListener('error', () => {
                reject(new Error('S3 upload failed - network error'));
            });
            
            xhr.addEventListener('abort', () => {
                reject(new Error('S3 upload aborted'));
            });
            
            xhr.open('PUT', presignedUrl);
            xhr.setRequestHeader('Content-Type', contentType);
            xhr.send(file);
        });
    },
    
    /**
     * Combined method: Get presigned URL and upload file to S3
     * Automatically uses multipart chunked upload for files > 10MB
     * @param {File} file - The file to upload
     * @param {string} context - Upload context: 'profile', 'banner', 'post', 'message'
     * @param {function} onProgress - Progress callback (0-100)
     * @returns {Promise<{uploadId, s3Key, contentType}>}
     */
    uploadFileDirectToS3: async (file, context, onProgress = null) => {
        // Sanitize the filename
        const sanitizedFile = sanitizeFile(file);
        const filename = sanitizedFile.name;
        const contentType = sanitizedFile.type || 'application/octet-stream';
        
        // TODO: File hash disabled — not used by backend yet.
        // Re-enable when backend supports dedup via hash.
        // See: computeFileHash() in fileHashWorker.js, hash-worker.js (streaming SHA-256)
        const fileHash = null;
        
        // Route large files through multipart chunked upload
        if (sanitizedFile.size >= MULTIPART_THRESHOLD) {
            return mediaService.uploadChunked(sanitizedFile, context, onProgress, fileHash);
        }
        
        // Small files: single presigned PUT (existing flow)
        const presignData = await mediaService.getPresignedUploadUrl(
            filename, 
            contentType, 
            context, 
            sanitizedFile.size
        );
        
        await mediaService.uploadToS3(
            presignData.presignedUrl, 
            sanitizedFile, 
            contentType, 
            onProgress
        );
        
        return {
            uploadId: presignData.uploadId,
            s3Key: presignData.s3Key,
            contentType: contentType,
            filename: filename,
            fileSize: sanitizedFile.size,
            fileHash
        };
    },

    // ==================== MULTIPART CHUNKED UPLOAD METHODS ====================

    /**
     * Initialize a multipart upload on the backend
     */
    initMultipartUpload: async (filename, contentType, context, fileSize) => {
        const response = await api.post('/media/multipart/init', {
            filename,
            contentType,
            context,
            fileSize
        });
        return response.data;
    },

    /**
     * Complete a multipart upload after all chunks are uploaded
     */
    completeMultipartUpload: async (s3Key, s3UploadId, parts) => {
        const response = await api.post('/media/multipart/complete', {
            s3Key,
            s3UploadId,
            parts: parts.map(p => ({ partNumber: p.partNumber, eTag: p.eTag }))
        });
        return response.data;
    },

    /**
     * Abort a multipart upload (cleanup)
     */
    abortMultipartUpload: async (s3Key, s3UploadId) => {
        try {
            await api.post('/media/multipart/abort', { s3Key, s3UploadId });
        } catch (err) {
            console.warn('Failed to abort multipart upload:', err.message);
        }
    },

    /**
     * Upload a single chunk to S3 via presigned URL with retry logic
     * @returns {{ partNumber: number, eTag: string }}
     */
    uploadChunk: (partUrl, chunk, partNumber, retries = MAX_CHUNK_RETRIES) => {
        return new Promise((resolve, reject) => {
            const attempt = (attemptsLeft) => {
                const xhr = new XMLHttpRequest();

                xhr.addEventListener('load', () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        const eTag = xhr.getResponseHeader('ETag');
                        if (!eTag) {
                            reject(new Error(`Chunk ${partNumber} upload succeeded but ETag header missing — check S3 CORS ExposeHeaders config`));
                            return;
                        }
                        resolve({ partNumber, eTag: eTag.replace(/"/g, '') });
                    } else if (attemptsLeft > 0) {
                        const delay = (MAX_CHUNK_RETRIES - attemptsLeft + 1) * 1000;
                        console.warn(`Chunk ${partNumber} failed (${xhr.status}), retrying in ${delay}ms...`);
                        setTimeout(() => attempt(attemptsLeft - 1), delay);
                    } else {
                        reject(new Error(`Chunk ${partNumber} upload failed with status ${xhr.status}`));
                    }
                });

                xhr.addEventListener('error', () => {
                    if (attemptsLeft > 0) {
                        const delay = (MAX_CHUNK_RETRIES - attemptsLeft + 1) * 1000;
                        console.warn(`Chunk ${partNumber} network error, retrying in ${delay}ms...`);
                        setTimeout(() => attempt(attemptsLeft - 1), delay);
                    } else {
                        reject(new Error(`Chunk ${partNumber} upload failed - network error after ${MAX_CHUNK_RETRIES} retries`));
                    }
                });

                xhr.open('PUT', partUrl);
                xhr.send(chunk);
            };

            attempt(retries);
        });
    },

    /**
     * Save upload progress to localStorage for resume capability
     */
    saveUploadProgress: (uploadId, progressData) => {
        try {
            localStorage.setItem(
                UPLOAD_STORAGE_PREFIX + uploadId,
                JSON.stringify({ ...progressData, updatedAt: Date.now() })
            );
        } catch (e) {
            console.warn('Could not save upload progress:', e.message);
        }
    },

    /**
     * Load upload progress from localStorage
     */
    loadUploadProgress: (uploadId) => {
        try {
            const data = localStorage.getItem(UPLOAD_STORAGE_PREFIX + uploadId);
            if (!data) return null;
            const parsed = JSON.parse(data);
            // Expire old entries
            if (Date.now() - parsed.updatedAt > UPLOAD_EXPIRY_MS) {
                localStorage.removeItem(UPLOAD_STORAGE_PREFIX + uploadId);
                return null;
            }
            return parsed;
        } catch {
            return null;
        }
    },

    /**
     * Clear upload progress from localStorage
     */
    clearUploadProgress: (uploadId) => {
        try {
            localStorage.removeItem(UPLOAD_STORAGE_PREFIX + uploadId);
        } catch {
            // ignore
        }
    },

    /**
     * Full chunked upload orchestrator with resume, progress, and Wake Lock
     * @param {File} file - The file to upload
     * @param {string} context - Upload context
     * @param {function} onProgress - Progress callback (0-100)
     * @param {string|null} fileHash - Pre-computed file hash for integrity/dedup
     * @returns {Promise<{uploadId, s3Key, contentType, filename, fileSize, fileHash}>}
     */
    uploadChunked: async (file, context, onProgress = null, fileHash = null) => {
        const filename = file.name;
        const contentType = file.type || 'application/octet-stream';

        // Acquire wake lock to prevent screen sleep during upload
        await acquireWakeLock();

        let initData;
        try {
            // Step 1: Initialize multipart upload
            initData = await mediaService.initMultipartUpload(filename, contentType, context, file.size);
        } catch (err) {
            releaseWakeLock();
            throw err;
        }

        const { uploadId, s3UploadId, s3Key, partUrls, totalParts, chunkSize } = initData;
        const completedParts = [];
        let uploadedCount = 0;

        // Check for resumable progress
        const savedProgress = mediaService.loadUploadProgress(uploadId);
        const alreadyCompleted = new Set();
        if (savedProgress && savedProgress.s3UploadId === s3UploadId) {
            savedProgress.completedParts.forEach(p => {
                completedParts.push(p);
                alreadyCompleted.add(p.partNumber);
            });
            uploadedCount = alreadyCompleted.size;
        }

        // Report initial progress for resumed uploads
        if (onProgress && uploadedCount > 0) {
            onProgress(Math.round((uploadedCount / totalParts) * 100));
        }

        // Step 2: Upload chunks in parallel batches with auto-resume on tab suspension
        let autoResumeAttempts = 0;

        const uploadRemainingChunks = async (pending) => {
            for (let i = 0; i < pending.length; i += MAX_CONCURRENT_CHUNKS) {
                const batch = pending.slice(i, i + MAX_CONCURRENT_CHUNKS);

                const batchResults = await Promise.all(
                    batch.map(({ partNumber, partUrl }) => {
                        const start = (partNumber - 1) * chunkSize;
                        const end = Math.min(start + chunkSize, file.size);
                        const chunk = file.slice(start, end);
                        return mediaService.uploadChunk(partUrl, chunk, partNumber);
                    })
                );

                // Record completed chunks
                for (const result of batchResults) {
                    completedParts.push(result);
                    alreadyCompleted.add(result.partNumber);
                    uploadedCount++;
                }

                // Update progress
                if (onProgress) {
                    onProgress(Math.round((uploadedCount / totalParts) * 100));
                }

                // Persist progress for resume capability
                mediaService.saveUploadProgress(uploadId, {
                    s3Key,
                    s3UploadId,
                    totalParts,
                    chunkSize,
                    completedParts: [...completedParts],
                    fileSize: file.size,
                    fileName: filename,
                    context
                });
            }
        };

        // Build initial pending list (excluding already-completed from resume)
        const buildPendingList = () => {
            const pending = [];
            for (let i = 0; i < totalParts; i++) {
                const partNumber = i + 1;
                if (!alreadyCompleted.has(partNumber)) {
                    pending.push({ partNumber, partUrl: partUrls[i] });
                }
            }
            return pending;
        };

        try {
            let remaining = buildPendingList();

            while (remaining.length > 0) {
                try {
                    await uploadRemainingChunks(remaining);
                    remaining = []; // All done
                } catch (err) {
                    // Check if this is likely a tab suspension (network error while hidden)
                    const isTabSuspension = 
                        typeof document !== 'undefined' &&
                        document.visibilityState === 'hidden' &&
                        (err.message?.includes('network error') || err.message?.includes('abort'));

                    if (isTabSuspension && autoResumeAttempts < MAX_AUTO_RESUME_ATTEMPTS) {
                        autoResumeAttempts++;

                        // Wait for user to come back to the app
                        await waitForVisibility();

                        // Re-acquire wake lock
                        await acquireWakeLock();

                        // Rebuild pending list from what's still not completed
                        remaining = buildPendingList();

                        if (onProgress && uploadedCount > 0) {
                            onProgress(Math.round((uploadedCount / totalParts) * 100));
                        }
                    } else {
                        // Not a tab suspension or too many retries — fail for real
                        throw err;
                    }
                }
            }
        } catch (err) {
            releaseWakeLock();
            // Don't clear progress — allow manual resume on retry
            console.error('Chunked upload failed:', err.message);
            throw err;
        }

        // Step 3: Complete the multipart upload
        try {
            // Sort parts by partNumber (S3 requires ordered list)
            completedParts.sort((a, b) => a.partNumber - b.partNumber);
            await mediaService.completeMultipartUpload(s3Key, s3UploadId, completedParts);
        } catch (err) {
            releaseWakeLock();
            // Abort the upload on completion failure
            await mediaService.abortMultipartUpload(s3Key, s3UploadId);
            mediaService.clearUploadProgress(uploadId);
            throw err;
        }

        // Cleanup
        releaseWakeLock();
        mediaService.clearUploadProgress(uploadId);

        return {
            uploadId,
            s3Key,
            contentType,
            filename,
            fileSize: file.size,
            fileHash
        };
    },

    /**
     * Process an uploaded media file (HEIC conversion, thumbnail generation)
     * Call this AFTER uploadFileDirectToS3 to get thumbnail for preview
     * @param {string} s3Key - The S3 key returned from uploadFileDirectToS3
     * @param {string} context - Upload context: 'post', 'message' (thumbnails generated for these)
     * @param {string} uploadId - Optional upload ID for tracking
     * @returns {Promise<{processId, processedS3Key, originalS3Key, thumbnails, mediaType, converted, width, height}>}
     */
    processMedia: async (s3Key, context, uploadId = null) => {
        const response = await api.post('/media/process', {
            s3Key,
            context,
            uploadId
        });
        
        return response.data;
    },

    /**
     * Combined method: Upload file and process it (for posts/messages)
     * Returns the processed s3Key and thumbnail URL(s) for preview
     * - For images: single thumbnail
     * - For videos: multiple preview frames (user can select)
     * @param {File} file - The file to upload
     * @param {string} context - 'post' or 'message' 
     * @param {function} onProgress - Progress callback (0-100)
     * @returns {Promise<{s3Key, processedS3Key, thumbnails[], thumbnailUrl, mediaType, width, height, converted, duration}>}
     */
    uploadAndProcessMedia: async (file, context, onProgress = null) => {
        // Step 1: Upload to S3
        const uploadResult = await mediaService.uploadFileDirectToS3(file, context, (progress) => {
            // Scale progress to 0-80% for upload, leaving 20% for processing
            if (onProgress) onProgress(Math.round(progress * 0.8));
        });
        
        // Step 2: Process the uploaded file (Lambda handles conversion + thumbnails)
        if (onProgress) onProgress(85);
        const processResult = await mediaService.processMedia(
            uploadResult.s3Key,
            context,
            uploadResult.uploadId
        );
        
        if (onProgress) onProgress(100);
        
        // Return combined result with all thumbnails
        // For videos, thumbnails[] contains multiple preview frames for selection
        return {
            uploadId: uploadResult.uploadId,
            s3Key: uploadResult.s3Key,
            processedS3Key: processResult.processedS3Key,
            // All thumbnails (for video preview selection)
            thumbnails: processResult.thumbnails || [],
            // First thumbnail URL for immediate preview
            thumbnailUrl: processResult.thumbnails?.[0]?.url || null,
            thumbnailS3Key: processResult.thumbnails?.[0]?.s3Key || null,
            // Media metadata
            mediaType: processResult.mediaType,
            width: processResult.width,
            height: processResult.height,
            duration: processResult.duration,
            converted: processResult.converted,
            originalMimeType: processResult.originalMimeType,
            processedMimeType: processResult.processedMimeType,
            fileSize: processResult.fileSize,
            filename: uploadResult.filename
        };
    },
    
    getMediaUrl: (mediaPath) => {
        if (!mediaPath) return '';
        // CloudFront URLs should be provided directly by the backend
        if (mediaPath.startsWith('http')) return mediaPath;
        
        console.warn('Media path without full URL detected:', mediaPath);
        return mediaPath;
    },
    
    deleteMedia: async (mediaId) => {
        await api.delete(`/media/${mediaId}`);
    },
    
    getMediaStatus: async (mediaId) => {
        const response = await api.get(`/media/${mediaId}/status`);
        return response.data;
    },

    // ==================== UPLOAD LIBRARY ====================

    /**
     * Fetch the current user's Upload Library (previously uploaded & approved media).
     * @param {object} options
     * @param {number} [options.page=0] - 0-based page number
     * @param {number} [options.size=20] - Page size (max 50)
     * @param {string|null} [options.mediaType] - Filter: IMAGE | VIDEO | AUDIO | null for all
     * @param {string|null} [options.search] - Optional filename search string
     * @returns {Promise<{content, totalElements, totalPages, number}>}
     */
    getUploadLibrary: async ({ page = 0, size = 20, mediaType = null, search = null, sortBy = 'createdAt', sortDir = 'desc', dateFrom = null, dateTo = null } = {}) => {
        const params = { page, size, sortBy, sortDir };
        if (mediaType) params.mediaType = mediaType;
        if (search && search.trim()) params.search = search.trim();
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
        const response = await api.get('/media/library', { params });
        return response.data;
    }

    // Note: We don't need front-end access check methods because:
    // 1. The back-end already filters media based on user permissions
    // 2. The back-end only returns media DTOs the user has access to
    // 3. URLs provided by the back-end are signed to enforce access control
};

export default mediaService;